import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../../api/axiosConfig";
import { blogImageSrc, BLOG_PLACEHOLDER } from "../../api/blogImage";
import Breadcrumb from "../../components/Breadcrumb";

function NotesShell({ title, trail, loading, error, empty, children }) {
  return (
    <>
      <Breadcrumb title={title} path="Exam wise notes" />
      <section className="section-space exam-notes-page">
        <div className="container">
          <p className="exam-note-trail">{trail}</p>
          {loading ? <p className="text-center">Loading...</p> : null}
          {!loading && error ? (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          ) : null}
          {!loading && !error && empty ? (
            <div className="alert alert-info text-center" role="alert">
              {empty}
            </div>
          ) : null}
          {!loading && !error && !empty ? children : null}
        </div>
      </section>
    </>
  );
}

function NoteGrid({ items }) {
  return (
    <div className="row g-4">
      {items.map((item) => (
        <div className="col-md-6 col-lg-4" key={item.key}>
          <Link to={item.to} className="exam-note-card">
            {item.image ? (
              <img
                className="exam-note-card-photo"
                src={item.image}
                alt=""
                onError={(event) => {
                  event.target.onerror = null;
                  event.target.src = blogImageSrc(null, BLOG_PLACEHOLDER);
                }}
              />
            ) : null}
            <span className="exam-note-kicker">{item.kicker}</span>
            <h3>{item.title}</h3>
            {item.meta ? <p>{item.meta}</p> : null}
          </Link>
        </div>
      ))}
    </div>
  );
}

const typeText = (types) => (Array.isArray(types) && types.length ? types.join(", ") : "");

export function ExamListPage() {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .get("/exam-notes/exams")
      .then((res) => {
        if (!cancelled) setExams(res.data.exams || []);
      })
      .catch(() => {
        if (!cancelled) setError("Unable to load exams.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <NotesShell
      title="Exam wise notes"
      trail={<Link to="/">Home</Link>}
      loading={loading}
      error={error}
      empty={!exams.length ? "No exams available right now." : ""}
    >
      <NoteGrid
        items={exams.map((exam) => ({
          key: exam.id,
          to: `/exam-notes/${exam.id}`,
          kicker: "Exam",
          title: exam.name,
          meta: typeText(exam.examType),
        }))}
      />
    </NotesShell>
  );
}

export function ExamSubjectsPage() {
  const { examId } = useParams();
  const [exam, setExam] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/exam-notes/exams/${examId}/subjects`)
      .then((res) => {
        if (cancelled) return;
        setExam(res.data.exam || null);
        setSubjects(res.data.subjects || []);
      })
      .catch(() => {
        if (!cancelled) setError("Unable to load subjects.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [examId]);

  return (
    <NotesShell
      title={exam?.name || "Subjects"}
      trail={
        <>
          <Link to="/exam-notes">Exam wise notes</Link>
          <span> / {exam?.name || "Exam"}</span>
        </>
      }
      loading={loading}
      error={error}
      empty={!subjects.length ? "No subjects for this exam." : ""}
    >
      <NoteGrid
        items={subjects.map((subject) => ({
          key: subject.id,
          to: `/exam-notes/${examId}/subjects/${subject.id}`,
          kicker: "Subject",
          title: subject.name,
          meta: typeText(subject.examType),
          image: blogImageSrc(subject.photo, BLOG_PLACEHOLDER),
        }))}
      />
    </NotesShell>
  );
}

export function ExamTopicsPage() {
  const { examId, subjectId } = useParams();
  const [subject, setSubject] = useState(null);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/exam-notes/subjects/${subjectId}/topics`)
      .then((res) => {
        if (cancelled) return;
        setSubject(res.data.subject || null);
        setTopics(res.data.topics || []);
      })
      .catch(() => {
        if (!cancelled) setError("Unable to load topics.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [subjectId]);

  return (
    <NotesShell
      title={subject?.name || "Topics"}
      trail={
        <>
          <Link to="/exam-notes">Exam wise notes</Link>
          <span> / </span>
          <Link to={`/exam-notes/${examId}`}>Subjects</Link>
          <span> / {subject?.name || "Subject"}</span>
        </>
      }
      loading={loading}
      error={error}
      empty={!topics.length ? "No topics for this subject." : ""}
    >
      <NoteGrid
        items={topics.map((topic) => ({
          key: topic.id,
          to: `/exam-notes/${examId}/subjects/${subjectId}/topics/${topic.id}`,
          kicker: "Topic",
          title: topic.topic,
        }))}
      />
    </NotesShell>
  );
}

export function ExamSubtopicsPage() {
  const { examId, subjectId, topicId } = useParams();
  const [topic, setTopic] = useState(null);
  const [subtopics, setSubtopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/exam-notes/topics/${topicId}/subtopics`)
      .then((res) => {
        if (cancelled) return;
        setTopic(res.data.topic || null);
        setSubtopics(res.data.subtopics || []);
      })
      .catch(() => {
        if (!cancelled) setError("Unable to load subtopics.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [topicId]);

  return (
    <NotesShell
      title={topic?.topic || "Subtopics"}
      trail={
        <>
          <Link to="/exam-notes">Exam wise notes</Link>
          <span> / </span>
          <Link to={`/exam-notes/${examId}`}>Subjects</Link>
          <span> / </span>
          <Link to={`/exam-notes/${examId}/subjects/${subjectId}`}>Topics</Link>
        </>
      }
      loading={loading}
      error={error}
      empty={!subtopics.length ? "No subtopics for this topic." : ""}
    >
      <NoteGrid
        items={subtopics.map((item) => ({
          key: item.id,
          to: `/exam-notes/${examId}/subjects/${subjectId}/topics/${topicId}/subtopics/${item.id}`,
          kicker: "Subtopic",
          title: item.subtopic,
        }))}
      />
    </NotesShell>
  );
}

export function ExamContentsPage() {
  const { examId, subjectId, topicId, subtopicId } = useParams();
  const [subtopic, setSubtopic] = useState(null);
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/exam-notes/subtopics/${subtopicId}/contents`, {
        params: { examId },
      })
      .then((res) => {
        if (cancelled) return;
        setSubtopic(res.data.subtopic || null);
        setContents(res.data.contents || []);
      })
      .catch(() => {
        if (!cancelled) setError("Unable to load notes.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [subtopicId, examId]);

  return (
    <NotesShell
      title={subtopic?.subtopic || "Notes"}
      trail={
        <>
          <Link to="/exam-notes">Exam wise notes</Link>
          <span> / </span>
          <Link to={`/exam-notes/${examId}/subjects/${subjectId}/topics/${topicId}`}>
            Subtopics
          </Link>
        </>
      }
      loading={loading}
      error={error}
      empty={!contents.length ? "No notes for this subtopic." : ""}
    >
      <NoteGrid
        items={contents.map((item) => ({
          key: item.id,
          to: `/exam-notes/content/${item.id}`,
          kicker: "Note",
          title: item.heading,
        }))}
      />
    </NotesShell>
  );
}
